import {
  useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { RoomContext } from '@livekit/components-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, VStack } from '@gluestack-ui/themed';
import { Room } from 'livekit-client';

import '@/common/i18n';
import {
  resetServerTimeOffsetForTests,
  setServerTimeOffsetForTests,
} from '@/common/time/serverNow';
import { MeetingConfirmProvider } from '@/common/components/meeting/MeetingConfirm';
import { OralExamLabOverridesProvider } from '@/common/oralExam/oralExamLabOverrides';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import { LecturerConductingStage } from '@/lecturer/SlotManager/ProctorExamPanel/LecturerConductingStage';
import { LecturerPreflightStage } from '@/lecturer/SlotManager/ProctorExamPanel/LecturerPreflightStage';
import { OralExamConductingControls } from '@/lecturer/SlotManager/ProctorExamPanel/OralExamControlPanel';
import { CohortConsole } from '@/lecturer/SlotManager/ProctorExamPanel/CohortConsole';
import { StudentExamShell } from '@/student/module/components/StudentExamShell';

import { buildPreflightLabFixture } from '@/visual-lab/chapters/patrol/buildPreflightLabFixture';
import { GroupBriefingLabScreen } from '@/visual-lab/chapters/patrol/GroupBriefingLabScreen';
import {
  buildPatrolLabFixture,
  PATROL_LAB_SUPERVISOR_NAME,
  type PatrolLabFixture,
} from '@/visual-lab/chapters/patrol/buildPatrolLabFixture';
import { PATROL_LAB_STANDINS } from '@/visual-lab/chapters/patrol/patrolLabStandIns';
import { parsePatrolLabQuery } from '@/visual-lab/chapters/patrol/parsePatrolLabQuery';

const noopAsync = async () => undefined;
const noop = () => undefined;

/** Lab-only: move here-with-you onto the student the lecturer just chose. */
function withObservedStudent(
  fixture: PatrolLabFixture,
  bookingId: string | null,
): PatrolLabFixture {
  const { cohortState } = fixture.overrides;
  if (!bookingId || !cohortState) return fixture;
  const bookingRuns = cohortState.bookingRuns.map((run) => ({
    ...run,
    supervisorStatus: run.bookingId === bookingId
      ? OralExamSupervisorStatus.here_with_you
      : run.supervisorStatus === OralExamSupervisorStatus.here_with_you
        ? OralExamSupervisorStatus.watching
        : run.supervisorStatus,
  }));
  const cohort = { ...cohortState.cohort, bookingRuns };
  return {
    ...fixture,
    overrides: {
      ...fixture.overrides,
      observeBookingId: bookingId,
      cohortState: {
        ...cohortState,
        cohort,
        bookingRuns,
      },
    },
  };
}

function createLabQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
}

function PatrolLabRoom({ children }: { children: ReactNode }) {
  const room = useMemo(() => new Room(), []);
  useEffect(() => () => {
    room.disconnect();
  }, [room]);
  return <RoomContext.Provider value={room}>{children}</RoomContext.Provider>;
}

/**
 * Oral Exam lab — production CohortConsole / StudentExamShell / Engage stage
 * with e2e Harry / Hermione / Snape stand-ins instead of LiveKit.
 */
export function PatrolLabScreen({ search = '' }: { search?: string }) {
  const query = useMemo(() => parsePatrolLabQuery(search), [search]);
  const preflightFixture = useMemo(
    () => (query.stage === 'preflight' ? buildPreflightLabFixture(query) : null),
    [query],
  );
  const fixture = useMemo(
    () => (
      query.stage === 'preflight' || query.stage === 'briefing'
        ? null
        : buildPatrolLabFixture(query)
    ),
    [query],
  );
  const [walkedBookingId, setWalkedBookingId] = useState<string | null>(null);
  useEffect(() => {
    setWalkedBookingId(null);
  }, [search]);
  const walkedFixture = useMemo(
    () => (fixture ? withObservedStudent(fixture, walkedBookingId) : null),
    [fixture, walkedBookingId],
  );
  const queryClient = useMemo(() => createLabQueryClient(), []);

  useEffect(() => {
    if (query.skew === undefined) return undefined;
    setServerTimeOffsetForTests(-query.skew * 1000);
    return () => {
      resetServerTimeOffsetForTests();
    };
  }, [query.skew]);

  if (query.stage === 'briefing') {
    return (
      <Box flex={1} minHeight={0} width="100%" testID="patrol-lab-payload">
        <GroupBriefingLabScreen />
      </Box>
    );
  }

  let payload: ReactNode;
  if (preflightFixture) {
    payload = (
      <LecturerPreflightStage
        slotId={preflightFixture.slotId}
        slotStartIso={preflightFixture.slotStartIso}
        slotEndIso={preflightFixture.slotEndIso}
        joinSettings={preflightFixture.joinSettings}
        joinCreds={preflightFixture.joinCreds}
      />
    );
  } else if (walkedFixture?.payloadKind === 'console') {
    payload = (
      <CohortConsole
        slotId={walkedFixture.slotId}
        onDropIn={noop}
        onObserve={async (bookingId) => {
          setWalkedBookingId(bookingId);
        }}
        onHide={noopAsync}
        onLeave={noopAsync}
        onReset={noopAsync}
        onCancel={noopAsync}
      />
    );
  } else if (walkedFixture?.payloadKind === 'conducting') {
    payload = (
      <VStack flex={1} minHeight={0} px="$4" py="$3" space="md" justifyContent="space-between">
        <Box flex={1} minHeight={0}>
          <LecturerConductingStage
            question={walkedFixture.engagedQuestion?.question}
            questionId={walkedFixture.engagedQuestion?.questionId}
            secondsPerQuestion={120}
            questionMarkerStartedAt={walkedFixture.engagedStartedAt}
            questionDeadline={walkedFixture.engagedDeadline}
            isPaused={walkedFixture.engagedPaused}
            questionIndex={1}
            totalQuestions={3}
            studentId={walkedFixture.engagedStudentId}
          />
        </Box>
        <OralExamConductingControls
          ids={walkedFixture.engagedIds}
          runState={walkedFixture.engagedRunState}
          stateQueryKey={['patrol-lab-run', walkedFixture.engagedIds.bookingId]}
          isDropIn
          onReturnToOverview={noop}
        />
      </VStack>
    );
  } else if (walkedFixture) {
    payload = (
      <StudentExamShell
        runState={walkedFixture.studentRunState}
        studentFirstName={walkedFixture.studentFirstName}
        bookingId={walkedFixture.studentBookingId}
        supervisorName={PATROL_LAB_SUPERVISOR_NAME}
        supervisorPhotoUrl={PATROL_LAB_STANDINS.snape}
        onAbort={noop}
        onLeave={noop}
      />
    );
  }

  const overrides = preflightFixture?.overrides ?? walkedFixture?.overrides;
  if (!overrides || !payload) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MeetingConfirmProvider>
        <OralExamLabOverridesProvider value={overrides}>
          <PatrolLabRoom>
            <Box flex={1} minHeight={0} width="100%" testID="patrol-lab-payload">
              {payload}
            </Box>
          </PatrolLabRoom>
        </OralExamLabOverridesProvider>
      </MeetingConfirmProvider>
    </QueryClientProvider>
  );
}
